import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import { productsApi } from '../../services/apiService';
import MobileBottomNav from '../../components/MobileBottomNav/MobileBottomNav';
import './MobileWorldwidePage.css';

const REGION_OPTIONS = [
  { id: 'Global', label: 'Global', shortLabel: 'Global', flag: null },
  { id: 'Hong Kong, China', label: 'Hong Kong, China', shortLabel: 'Hong Kong, China', flag: 'https://flagcdn.com/w40/hk.png' },
  { id: 'India', label: 'India', shortLabel: 'India', flag: 'https://flagcdn.com/w40/in.png' },
  { id: 'Europe', label: 'Europe', shortLabel: 'Europe', flag: null },
  { id: 'Taiwan, China', label: 'Taiwan, China', shortLabel: 'Taiwan, China', flag: 'https://flagcdn.com/w40/tw.png' },
  { id: 'Japan', label: 'Japan', shortLabel: 'Japan', flag: 'https://flagcdn.com/w40/jp.png' },
  { id: 'United States', label: 'United States', shortLabel: 'United States', flag: 'https://flagcdn.com/w40/us.png' },
  { id: 'Pakistan', label: 'Pakistan', shortLabel: 'Pakistan', flag: 'https://flagcdn.com/w40/pk.png' },
  { id: 'Vietnam', label: 'Vietnam', shortLabel: 'Vietnam', flag: 'https://flagcdn.com/w40/vn.png' },
  { id: 'Singapore', label: 'Singapore', shortLabel: 'Singapore', flag: 'https://flagcdn.com/w40/sg.png' },
  { id: 'South Korea', label: 'South Korea', shortLabel: 'South Korea', flag: 'https://flagcdn.com/w40/kr.png' },
  { id: 'Thailand', label: 'Thailand', shortLabel: 'Thailand', flag: 'https://flagcdn.com/w40/th.png' },
  { id: 'Malaysia', label: 'Malaysia', shortLabel: 'Malaysia', flag: 'https://flagcdn.com/w40/my.png' },
  { id: 'Indonesia', label: 'Indonesia', shortLabel: 'Indonesia', flag: 'https://flagcdn.com/w40/id.png' },
  { id: 'Turkiye', label: 'Turkiye', shortLabel: 'Turkiye', flag: 'https://flagcdn.com/w40/tr.png' }
];

const BANNERS = [
  {
    id: 'used-phones',
    region: 'Hong Kong, China',
    title: `Hong Kong's (China) Used Phones: Top Value`,
    stats: '4M+ searches',
    theme: 'copper',
    image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=600&auto=format&fit=crop&q=60',
    thumbs: [
      'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1565849904461-04a58ad377e0?w=200&auto=format&fit=crop&q=60'
    ]
  },
  {
    id: 'german-fizz',
    region: 'Germany',
    title: 'German Finds: Crafted Taste & Premium Goods',
    stats: '115K+ searches',
    theme: 'berry',
    image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&auto=format&fit=crop&q=60',
    thumbs: [
      'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1481391319762-47dff72954d9?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1461823385004-d7660947a7c0?w=200&auto=format&fit=crop&q=60'
    ]
  },
  {
    id: 'india-export',
    region: 'India',
    title: 'India exports: Fast-moving picks buyers keep sourcing',
    stats: '910K+ searches',
    theme: 'teal',
    image: 'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=600&auto=format&fit=crop&q=60',
    thumbs: [
      'https://images.unsplash.com/photo-1583394838336-acd977736f90?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1604948501466-4e9c339b9c24?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1580910051074-3eb694886505?w=200&auto=format&fit=crop&q=60'
    ]
  },
  {
    id: 'japan-tech',
    region: 'Japan',
    title: 'Japan tech drops: Compact devices and smart accessories',
    stats: '540K+ searches',
    theme: 'midnight',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&auto=format&fit=crop&q=60',
    thumbs: [
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1588508065123-287b28e013da?w=200&auto=format&fit=crop&q=60',
      'https://images.unsplash.com/photo-1545239351-1141bd82e8a6?w=200&auto=format&fit=crop&q=60'
    ]
  }
];

const PICK_TAGS = ['Lower price', 'Up 6600%', 'Fast delivery', 'Hot item', 'Trending', 'Ready to ship'];

const resolveProductImage = (product) => {
  const imageValue =
    (Array.isArray(product?.productImages) && product.productImages[0]?.url) ||
    (Array.isArray(product?.images) ? product.images[0] : product?.images) ||
    product?.image ||
    product?.imageUrl ||
    product?.thumbnail ||
    null;

  if (!imageValue || typeof imageValue !== 'string') return null;
  if (imageValue.startsWith('http')) return imageValue;
  if (imageValue.startsWith('/uploads')) return imageValue;
  return `${API_BASE_URL}${imageValue.startsWith('/') ? imageValue : `/${imageValue}`}`;
};

const getProductTag = (product, index) => {
  if ((product?.seller?.memberYears || 0) >= 5) return 'Fast delivery';
  if ((product?.moq || 0) <= 10) return 'Lower price';
  return PICK_TAGS[index % PICK_TAGS.length];
};

const getPriceNumbers = (product) => {
  const min = product?.priceMin ?? product?.price ?? null;
  const max = product?.priceMax ?? null;
  return { min, max };
};

const getProductPriceLabel = (product, formatPrice, formatPriceRange) => {
  const { min, max } = getPriceNumbers(product);
  if (min !== null && max !== null && Number(min) !== Number(max)) {
    return formatPriceRange(min, max);
  }
  if (min !== null && min !== undefined && min !== '') {
    return formatPrice(min);
  }
  return null;
};

const MobileWorldwidePage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const bannerTrackRef = useRef(null);
  const { formatPrice, formatPriceRange } = useCurrency();
  const [activeTopTab, setActiveTopTab] = useState('Worldwide');
  const [query, setQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('Global');
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRegionSheetOpen, setIsRegionSheetOpen] = useState(false);
  const [bannerIndex, setBannerIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setBannerIndex((current) => (current + 1) % BANNERS.length);
    }, 3800);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!bannerTrackRef.current) return;
    const cardWidth = bannerTrackRef.current.clientWidth * 0.82;
    bannerTrackRef.current.scrollTo({ left: bannerIndex * (cardWidth + 12), behavior: 'smooth' });
  }, [bannerIndex]);

  useEffect(() => {
    if (!isRegionSheetOpen) {
      document.body.style.overflow = '';
      return undefined;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isRegionSheetOpen]);

  const filterProducts = (all, nextRegion, nextQuery) => {
    let result = all;
    if (nextRegion && nextRegion !== 'Global') {
      result = result.filter(
        (p) =>
          p.region === nextRegion ||
          p.country === nextRegion ||
          p.seller?.country === nextRegion
      );
    }
    if (nextQuery?.trim()) {
      const q = nextQuery.trim().toLowerCase();
      result = result.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q) ||
          (p.category || '').toLowerCase().includes(q) ||
          (p.seller?.name || '').toLowerCase().includes(q)
      );
    }
    return result;
  };

  const loadProducts = async (nextRegion = selectedRegion, nextQuery = '') => {
    try {
      setLoading(true);
      let all = allProducts;
      if (all.length === 0) {
        const response = await productsApi.getAll({ limit: 100 });
        all = Array.isArray(response) ? response : (response?.products || response?.data || []);
        setAllProducts(all);
      }
      setProducts(filterProducts(all, nextRegion, nextQuery));
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts(selectedRegion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allProducts.length > 0) {
      setProducts(filterProducts(allProducts, selectedRegion, query));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, allProducts, query]);

  const handleTopTabClick = (tab) => {
    setActiveTopTab(tab);
    if (tab === 'AI Mode') {
      navigate('/mobile/ai-mode');
      return;
    }
    if (tab === 'Worldwide') {
      navigate('/mobile/worldwide');
      return;
    }
    navigate('/', { state: { mobileNavTab: tab } });
  };

  const handleSearch = () => {
    setProducts(filterProducts(allProducts, selectedRegion, query));
  };

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      if (!transcript) return;
      setQuery(transcript);
      setProducts(filterProducts(allProducts, selectedRegion, transcript));
    };
    recognition.start();
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleImagePicked = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const nextQuery = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
    setQuery(nextQuery);
    setProducts(filterProducts(allProducts, selectedRegion, nextQuery));
  };

  const handleRegionSelect = (region) => {
    setSelectedRegion(region);
    setIsRegionSheetOpen(false);
  };

  const visibleRegionPills = useMemo(() => {
    const defaults = ['Global', 'Hong Kong, China', 'India', 'Europe'];
    const unique = [selectedRegion, ...defaults.filter((item) => item !== selectedRegion)];
    return unique.slice(0, 4).map((item) => REGION_OPTIONS.find((option) => option.id === item)).filter(Boolean);
  }, [selectedRegion]);

  const picks = products.slice(0, 8);
  const shippingProducts = products.filter((p) => p.freeShipping === true).slice(0, 8).concat(
    products.slice(8, 16)
  ).slice(0, 8);

  return (
    <div className="m-ali-home mw-page">
      <div className="mw-shell">
        <div className="m-ali-nav mw-top-tabs">
          {['AI Mode', 'Products', 'Manufacturers', 'Worldwide'].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`m-ali-nav-tab mw-top-tab ${activeTopTab === tab ? 'active is-active' : ''}`}
              onClick={() => handleTopTabClick(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="m-ali-search mw-search-wrap">
          <div className="m-ali-search-bar mw-search-bar">
            <button type="button" className="mw-search-icon-button m-ali-search-camera" onClick={handleCameraClick} aria-label="Search by image">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M4 7h4l1.5-2h5L16 7h4v11H4z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
            <div className="m-ali-search-input mw-search-input-wrap">
              <input
                type="text"
                className="mw-search-input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSearch();
                }}
                placeholder="Search worldwide products"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#999', fontSize: 18, lineHeight: 1 }}
                >
                  ×
                </button>
              )}
            </div>
            <button type="button" className="mw-search-icon-button m-ali-search-mic" onClick={handleVoiceSearch} aria-label="Voice search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2" />
                <path d="M12 19v3" />
                <path d="M8 22h8" />
              </svg>
            </button>
            <button type="button" className="m-ali-search-btn mw-search-submit" onClick={handleSearch} aria-label="Search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="11" cy="11" r="6.5" />
                <path d="M16.2 16.2L21 21" />
              </svg>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="mw-file-input" onChange={handleImagePicked} />
          </div>
        </div>

        <div className="mw-banner-strip" ref={bannerTrackRef}>
          {BANNERS.map((banner) => (
            <article key={banner.id} className={`mw-banner-card mw-banner-card--${banner.theme}`}>
              <div className="mw-banner-copy">
                <p className="mw-banner-title">{banner.title}</p>
                <div className="mw-banner-meta">
                  <span className="mw-banner-flag">{banner.region}</span>
                  <span>{banner.stats}</span>
                </div>
              </div>
              <img src={banner.image} alt={banner.title} className="mw-banner-hero" />
              <div className="mw-banner-thumbs">
                {banner.thumbs.map((thumb, index) => (
                  <div key={thumb} className="mw-banner-thumb">
                    <img src={thumb} alt={`${banner.title} ${index + 1}`} />
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mw-banner-dots">
          {BANNERS.map((banner, index) => (
            <button
              key={banner.id}
              type="button"
              className={`mw-banner-dot ${bannerIndex === index ? 'is-active' : ''}`}
              onClick={() => setBannerIndex(index)}
            />
          ))}
        </div>

        <div className="mw-region-row">
          {visibleRegionPills.map((region) => (
            <button
              key={region.id}
              type="button"
              className={`mw-region-pill ${selectedRegion === region.id ? 'is-active' : ''}`}
              onClick={() => handleRegionSelect(region.id)}
            >
              {region.flag ? <img src={region.flag} alt={region.label} className="mw-region-flag" /> : <span className="mw-region-globe">Global</span>}
              <span>{region.shortLabel}</span>
            </button>
          ))}
          <button type="button" className="mw-region-more" onClick={() => setIsRegionSheetOpen(true)} aria-label="Open region selector">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>

        <div className="m-ali-section">
          <div className="m-ali-section-hdr">
            <div>
              <h2 className="m-ali-section-title">Worldwide picks for you</h2>
            </div>
          </div>
          <div className="m-ali-scroll">
            {loading ? (
              <div className="m-ali-loading">Loading worldwide picks…</div>
            ) : picks.length === 0 ? (
              <div className="m-ali-loading">{query.trim() ? 'No results found.' : 'No worldwide products found.'}</div>
            ) : picks.map((product, index) => (
              <div key={product.id} className="m-ali-deal-card" onClick={() => navigate(`/mobile/product/${product.id}`)}>
                <div className="m-ali-deal-img">
                  <img
                    src={resolveProductImage(product) || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'}
                    alt={product.name}
                  />
                </div>
                <span className="m-ali-deal-price">{getProductPriceLabel(product, formatPrice, formatPriceRange) || '—'}</span>
                <span className="m-ali-deal-sub">{getProductTag(product, index)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="m-ali-section">
          <div className="m-ali-section-hdr">
            <div>
              <h2 className="m-ali-section-title">Free shipping on your first order</h2>
              <p className="m-ali-section-sub">Exclusive deals · Limited time offer</p>
            </div>
            <button type="button" className="m-ali-section-arrow" onClick={handleSearch} aria-label="View all">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </button>
          </div>
          <div className="m-ali-scroll">
            {loading ? (
              <div className="m-ali-loading">Loading products…</div>
            ) : shippingProducts.length === 0 ? (
              <div className="m-ali-loading">{query.trim() ? 'No results found.' : 'No free shipping picks for this region.'}</div>
            ) : shippingProducts.map((product, idx) => (
              <div key={product.id} className="m-ali-tail-card" onClick={() => navigate(`/mobile/product/${product.id}`)}>
                <div className="m-ali-tail-img">
                  <img
                    src={resolveProductImage(product) || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'}
                    alt={product.name}
                  />
                  <span className="m-ali-tail-price">{getProductPriceLabel(product, formatPrice, formatPriceRange) || '—'}</span>
                </div>
                <span className="m-ali-tail-name">{product.name}</span>
                <span className="m-ali-tail-views">{product.moq ? `Min. order: ${product.moq}` : 'Free shipping'}</span>
              </div>
            ))}
          </div>
        </div>

        <MobileBottomNav />

        {isRegionSheetOpen && (
          <div className="mw-sheet-overlay" role="presentation" onClick={() => setIsRegionSheetOpen(false)}>
            <div className="mw-sheet" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
              <div className="mw-sheet-handle" />
              <div className="mw-sheet-header">
                <h3>Global supply network</h3>
                <button type="button" className="mw-sheet-close" onClick={() => setIsRegionSheetOpen(false)} aria-label="Close region selector">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                  </svg>
                </button>
              </div>
              <div className="mw-sheet-list">
                {REGION_OPTIONS.map((region) => (
                  <button key={region.id} type="button" className="mw-sheet-row" onClick={() => handleRegionSelect(region.id)}>
                    <div className="mw-sheet-row-left">
                      {region.flag ? <img src={region.flag} alt={region.label} className="mw-sheet-flag" /> : <span className="mw-sheet-global">Global</span>}
                      <span>{region.label}</span>
                    </div>
                    <span className={`mw-sheet-radio ${selectedRegion === region.id ? 'is-active' : ''}`}>
                      <span className="mw-sheet-radio-inner" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileWorldwidePage;